/**
 * Types TypeScript pour la gestion des sociétés
 */

export interface Society {
  uuid: string;
  parentId: string | null;
  rootParentId?: string | null;
  rootParentName?: string | null;
  code: string;
  name: string;
  sign: string;
  siren: string | null;
  email: string;
  phone: string;
  address: string;
  /** Coordonnées GPS de l'adresse (géocodage Google) */
  lat?: number | null;
  lng?: number | null;
  googlePlaceId?: string | null;
  cp: string;
  city: string;
  logo?: string | null;
  kbis?: string | null;
  rib?: string | null;
  managementMandate?: string | null;
  warrantyLeaflet?: string | null;
  sepaMandate?: string | null;
  vendorType: number;
  isGroup: number;
  isTestMode: boolean;
  isIntermediary?: boolean;
  hierarchyLevel?: number;
  isActive: boolean;
  accountType: number;
  contractGuarantor: number;
  invoicedEntity: number;
  warrantyPolicy: number;
  canCreateContract: boolean;
  createdAt: string;
  updatedAt: string | null;
  
  // Options et Services (Step 3) — champs legacy sur PRO_SOCIETY
  defineProvision?: boolean;
  isMultilingual?: boolean;
  extendedDuration?: boolean;
  warrantyIncludedOption?: boolean;
  mandatoryVehicleCheck?: boolean;
  deliveryProofCreate?: number;
  monthlyPayment?: number;
  monthlyPlateformFees?: number;
  onlinePayment?: boolean;
  onlinePlateformFees?: number;
  isTransferPrice?: number;
  commission?: boolean;
  presaleSheet?: number;

  // Configuration V2 (from pro_v2_society_configuration, loaded with society detail)
  groupConfiguration?: any;
  ownConfiguration?: any;
  isConfigurationOwner?: boolean;

  // Relations optionnelles
  parent?: Society | null;
  children?: Society[];
  childrenCount?: number;
  parentsCount?: number;
  
  // Champs supplémentaires
  paymentMethod?: number;
  url?: string;
  accountingEmail?: string;
  savEmail?: string;
  orias?: string;
  
  // Responsable SAV
  savManagerFirstname?: string;
  savManagerLastname?: string;
  savManagerEmail?: string;
  savManagerPhone?: string;
  savGuidelines?: string;

  // Attribution (internal users)
  commercialId?: string;
  savManagerId?: string;
  
  // Stripe Connect — l'état du compte (chargesEnabled, requirements, etc.) vit
  // sur la config V2 et se lit via useEffectiveConfiguration().
  stripeAccountId?: string;


  // Pricing
  servicePrice?: number;
  customPricings?: CustomPricing[];

  // Informations Bancaires (RIB)
  ribDebtorName?: string;
  ribDebtorAddress?: string;
  ribDebtorCP?: string;
  ribDebtorCity?: string;
  ribBankName?: string;
  ribCountry?: string;
  ribIbanKey?: string;
  ribBankCode?: string;
  ribWindowCode?: string;
  ribAccountNumber?: string;
  ribKey?: string;
  ribBic?: string;
  iban?: string;
}

export interface CustomPricing {
  id: number;
  duration: number;
  price: number;
  createdAt: string;
  effectiveDate: string;
}

export type ServicePriceSource = 'own' | 'billed-parent' | 'catalog';

export interface CreatePricingPreview {
  warrantiesAffected: number;
  contractsAffected: number;
  contractsExcludedInvoiced: number;
  contractsExcludedOwnOverride: number;
  billedChildren: Array<{
    societyId: string;
    name: string | null;
    partnerId: number | null;
    contractsAffected: number;
  }>;
}

export interface DeletePricingPreview {
  entry: CustomPricing;
  oldPrice: number;
  newPrice: number | null;
  newSource: ServicePriceSource | null;
  warrantiesAffected: number;
  contractsAffected: number;
  billedChildren: Array<{
    societyId: string;
    name: string | null;
    contractsAffected: number;
  }>;
}

export interface CreatePricingResult {
  entry: CustomPricing;
  propagation: {
    warrantiesUpdated: number;
    contractsUpdated: number;
  };
}

export interface DeletePricingResult {
  deleted: CustomPricing;
  newPrice: number | null;
  newSource: ServicePriceSource | null;
  warrantiesUpdated: number;
  contractsUpdated: number;
}

export interface SocietyHierarchy {
  ancestors: Society[];
  descendants: Society[];
  root: Society;
}

export interface TreeNode {
  uuid: string;
  name: string;
  code: string;
  isGroup: number;
  isActive: boolean;
  isTestMode: boolean;
  children: TreeNode[];
}

export interface CreateSocietyDto {
  parentId?: string;
  companyCode: string;
  companyName: string;
  companySign?: string;
  siren?: string;
  email: string;
  phone: string;
  address: string;
  cp: string;
  city: string;
  vendorType: number;
  contractGuarantor: number;
  isTestMode?: boolean;
  accountingEmail?: string;
  savEmail?: string;
  orias?: string;
}

export type UpdateSocietyDto = Partial<CreateSocietyDto>

/**
 * Interface simplifiée pour les éléments de la liste des sociétés
 */
export interface SocietyListItem {
  uuid: string;
  rootParentName: string | null;
  name: string;
  vendorType: number;
  /** Accès à l'API publique v2 résolu : 1 = désactivé, 2 = activé */
  proVersion: number;
  createdAt: string;
  lastConnectionDate: string | null;
  isActive: boolean;
  paymentMethod: number;
  permanentTransfer: boolean;
  billedId: string | null;
  contractsCount: number;
  warrantiesCount: number;
}

export interface SocietyFilters {
  nameFilter?: string;
  vendorTypeFilter?: number;
  /** Filtre version pro : 1 = v1, 2 = v2 */
  proVersionFilter?: number;
  /** Filtres gestionnaire : UUID d'un user interne (commercial / SAV / superviseur) */
  commercialIdFilter?: string;
  savManagerIdFilter?: string;
  supervisorIdFilter?: string;
  isActiveFilter?: boolean;
  societyIds?: string;
  createdAfter?: string;
  createdBefore?: string;
  lastConnectionAfter?: string;
  lastConnectionBefore?: string;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
  page?: number;
  limit?: number;
}

export interface SocietyListResponse {
  societies: SocietyListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Interface pour les logs d'audit
 */
export interface AuditLog {
  id: string;
  userId?: string;
  userEmail?: string;
  userName?: string;
  entityType: string;
  entityId: string;
  action: string;
  oldData?: any;
  newData?: any;
  changesSummary?: string;
  ipAddress?: string;
  userAgent?: string;
  correlationId?: string;
  stepName?: string;
  stepNumber?: number;
  durationMs?: number;
  status: 'SUCCESS' | 'ERROR' | 'INFO' | 'IN_PROGRESS';
  createdAt: string;
}

/**
 * Interface pour les utilisateurs/administrateurs
 */
export interface User {
  uuid: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  roles: string[];
  isActive: boolean;
  createdAt: string;
  // Champs optionnels
  societyId?: string;
  company?: {
    uuid: string;
    name: string;
    code: string;
  };
}

/**
 * Interface pour un utilisateur avec informations de société enrichies
 * Utilisé pour afficher les utilisateurs avec leur société d'origine
 */
export interface UserWithSociety extends User {
  societyName: string;
  societyCode: string;
  isFromChild: boolean; // true si l'utilisateur vient d'une société enfant
  roleType: 'ADMIN' | 'USER'; // Type de rôle déduit des roles[]
}

/**
 * Interface pour la réponse des réparateurs
 */
export interface RepairersResponse {
  hasWorkshop: boolean;
  workshop: Society | null;
  partnerGarages: Society[];
}

// Enums/Constantes
export enum VehicleType {
  STANDARD = 'STANDARD',
  COLLECTION = 'COLLECTION',
  TWO_WHEELS = 'TWO_WHEELS',
}

export const VEHICLE_TYPE_LABELS: Record<VehicleType, string> = {
  [VehicleType.STANDARD]: 'Standard',
  [VehicleType.COLLECTION]: 'Collection',
  [VehicleType.TWO_WHEELS]: 'Deux-roues',
};

export const VENDOR_TYPES = {
  GARANT: 1,
  PRESTATAIRE: 2,
  BOTH: 3,
} as const;

export const VENDOR_TYPE_LABELS: Record<number, string> = {
  [VENDOR_TYPES.GARANT]: 'Garant',
  [VENDOR_TYPES.PRESTATAIRE]: 'Prestataire',
  [VENDOR_TYPES.BOTH]: 'Garant et Prestataire',
};

export const CONTRACT_GUARANTOR_TYPES = {
  COMPANY: 1,
  HEAD: 2,
} as const;

export const CONTRACT_GUARANTOR_LABELS: Record<number, string> = {
  [CONTRACT_GUARANTOR_TYPES.COMPANY]: 'Entreprise elle-même',
  [CONTRACT_GUARANTOR_TYPES.HEAD]: 'Tête de groupe',
};

export const GROUP_TYPES = {
  ALONE: 0,
  MEMBER: 1,
  HEAD: 2,
} as const;

export const GROUP_TYPE_LABELS: Record<number, string> = {
  [GROUP_TYPES.ALONE]: 'Entreprise seule',
  [GROUP_TYPES.MEMBER]: 'Membre d\'un groupe',
  [GROUP_TYPES.HEAD]: 'Tête de groupe',
};
